/**
 * M-16 · La regla del reparto de un precio de GRUPO, en un solo sitio del panel.
 *
 * Espejo de `App\Helpers\BudgetHelper` en el backend y de `FormatHelper` en el
 * funnel: los tres tienen que repartir EXACTAMENTE igual o el panel enseñaría un
 * importe distinto del que el servidor guarda.
 *
 * Nació dentro de `ExtraServiceSelect.vue` con M-15; se extrae aquí porque las
 * actividades (`TabBudgetActivities` / `TabActivityPackage`) necesitan la misma
 * regla y copiarla habría dejado tres versiones de un criterio que mueve dinero.
 */
export default class PriceHelper {
	/**
	 * ¿El precio de esta tarifa es del GRUPO? El flag puede llegar como número o
	 * como texto según el endpoint, de ahí el `Number()`.
	 */
	public static isGroupPrice(group) {
		return Number(group) === 1;
	}

	/**
	 * M-15: el importe de una tarifa de GRUPO se cobra en EUROS ENTEROS.
	 *
	 * Se redondea la LÍNEA —lo que paga el alumno por el concepto completo— y no el
	 * precio por día: el euro es demasiado grueso para una cifra diaria y con grupos
	 * grandes el desajuste se dispara. Mínimo 1 €: un concepto contratado no puede
	 * salir a 0,00 €.
	 */
	public static roundGroupAmount(amount) {
		const rounded = Math.round(amount);
		return amount > 0 && rounded < 1 ? 1 : rounded;
	}

	/** Divisor seguro: sin pasajeros conocidos no se reparte (espejo del backend). */
	public static peopleDivisor(people) {
		const n = Number(people) || 0;
		return n > 0 ? n : 1;
	}

	/**
	 * Lo que paga cada pasajero por una tarifa. Sin `group`, el precio se cobra tal
	 * cual, con sus céntimos: lo que el equipo escribe en el panel no se toca.
	 */
	public static amountPerPassenger(price, group, people) {
		const value = Number(price) || 0;
		if (!PriceHelper.isGroupPrice(group)) {
			return value;
		}
		return PriceHelper.roundGroupAmount(value / PriceHelper.peopleDivisor(people));
	}

	/**
	 * N-235 · Profesores que se cobran por exceder la cuota gratuita del destino.
	 * Espejo de `BudgetHelper::paidTeachers` en el backend (`ceil(t − s/f)`, que con
	 * profesores enteros es `t − floor(s/f)`). Sin cuota conocida, ninguno.
	 */
	public static paidTeachers(students, teachers, freeTeacherByStudents) {
		const alumnos = Number(students) || 0;
		const profesores = Number(teachers) || 0;
		const cuota = Number(freeTeacherByStudents) || 0;
		if (cuota <= 0 || profesores <= alumnos / cuota) {
			return 0;
		}
		return profesores - Math.floor(alumnos / cuota);
	}

	/**
	 * N-235 · El recargo POR ALUMNO de `count` personas cobradas como profesor —los de
	 * pago más las plazas de los servicios que viajan con el grupo—: el viaje de una
	 * persona (`sum`) más el suplemento del destino, repartido entre los alumnos. Es la
	 * regla de `BudgetService::setPriceBudget`, y como allí, la puerta es «hay a quien
	 * cobrar», no «hay más profesores que la cuota»: con 0 de pago, la plaza se cobra.
	 */
	public static teacherSupplement(sum, supplement, students, count) {
		const alumnos = Number(students) || 0;
		if (count <= 0 || alumnos <= 0) {
			return 0;
		}
		const value = Number(sum) || 0;
		return ((((Number(supplement) || 0) * value) / 100 + value) / alumnos) * count;
	}

	/**
	 * Varias LÍNEAS: lo que paga cada pasajero por un juego de tarifas (las de un día
	 * de la parrilla de actividades, o sus extras). Se redondea CADA LÍNEA y no la
	 * suma, porque el motor guarda una fila por tarifa.
	 *
	 * Mismo nombre que `FormatHelper.activityLinesTotal` en el funnel, a propósito:
	 * son la misma cuenta y así se ve de un vistazo que no pueden divergir.
	 */
	public static activityLinesTotal(prices, people) {
		return (prices || []).reduce(
			(sum, price) => sum + PriceHelper.amountPerPassenger(price.price, price.group, people),
			0
		);
	}
}
